const REAL_PRICE_FLAG = 'real-price-display'

const REAL_PRICE_VARIANTS = new Set<string>(['control', 'test'])

function isRealPriceVariant(variant: string | boolean | undefined): variant is RealPriceExperimentVariant {
  return typeof variant === 'string' && REAL_PRICE_VARIANTS.has(variant)
}

export type RealPriceExperimentVariant = 'control' | 'test'

/**
 * Instant Checkout Bounce — AB test: precio final (impuestos y tasas incluidos)
 * en la ficha de hotel para reducir rebotes rápidos del checkout.
 *
 * PostHog flag: `real-price-display` — variantes `control` | `test`.
 *
 * - `test`: UI muestra `total_price` / `total_price_label` (impuestos incluidos)
 * - `control`: UI muestra `price` / `price_label` (precio de siempre, sin tasas)
 *
 * Local: si PostHog aún no resuelve la flag, puedes forzar con
 * `LOCAL_VARIANT_OVERRIDE`. Mientras la flag exista, PostHog manda.
 */
const LOCAL_VARIANT_OVERRIDE: RealPriceExperimentVariant | null = null

/**
 * Resuelve la variante del AB. La flag de PostHog tiene prioridad;
 * el override local solo aplica si la flag aún no está disponible.
 */
export function resolveRealPriceVariant(
  flagVariant: string | boolean | undefined,
  localOverride: RealPriceExperimentVariant | null = null
): RealPriceExperimentVariant {
  if (isRealPriceVariant(flagVariant)) {
    return flagVariant
  }
  if (localOverride) {
    return localOverride
  }
  return 'control'
}

export function useRealPriceExperiment() {
  const flagVariant = useFeatureFlagVariantKey(REAL_PRICE_FLAG)

  const resolvedVariant = computed<RealPriceExperimentVariant>(() =>
    resolveRealPriceVariant(flagVariant.value, LOCAL_VARIANT_OVERRIDE)
  )

  /** Solo true en variante `test`: mostrar precio real con impuestos. */
  const isRealPriceDisplay = computed(() => resolvedVariant.value === 'test')

  const experimentVariant = computed(() => resolvedVariant.value)

  return { isRealPriceDisplay, experimentVariant }
}
